import { SLA_RUNTIME_REFRESH_TICK_MS } from "model/slaMonitorModel";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Politica di aggiornamento **runtime** delle superfici SLA (piano Client sez. 5
 * F3d): e' il meccanismo condiviso fra Monitor (`SlaMonitorSurface.tsx`) e
 * Diagramma (`SlaDiagramSurface.tsx`), cosi' la regola non nasce due volte.
 *
 * Cosa fornisce:
 * - `nowMs`: orologio locale che avanza di un tick (`SLA_RUNTIME_REFRESH_TICK_MS`,
 *   un secondo). Serve **solo** a rivalutare la freschezza (`as_of` contro il
 *   clock del browser) e la soglia dichiarata dal payload: senza di esso lo stato
 *   resterebbe verde finche' un'interazione non forza un ricalcolo (sez. 5 F3d).
 *   Non e' una simulazione del worker e non legge il calendario;
 * - `requestRefresh()`: rilettura esplicita, con la stessa semantica dei
 *   meccanismi automatici. Il consumatore decide **cosa** rileggere; qui non si
 *   azzera alcuno stato locale.
 *
 * Quando chiama `requestRefresh`:
 * - **rientro sul tab**: `visibilitychange` (pagina tornata visibile), `focus` e
 *   `pageshow` — gli stessi eventi gia' usati dalla superficie Attivita' (F3c);
 * - **soglia/apertura**: quando `needsRefetch(nowMs)` diventa vero, cioe' quando
 *   il clock del browser supera un `next_clock_change_at` del payload (pausa o
 *   ripresa del clock). La riapertura del calendario **non** si calcola nel
 *   browser: si richiede al Server (sez. 5 F3d).
 *
 * Limite dichiarato: il contratto SLA non espone alcun canale SSE (l'unico SSE
 * del client, `hooks/useSSE.ts`, e' lo stream dei task). Per le istanze e i nodi
 * SLA non esiste quindi un meccanismo push riusabile: la rilettura e' a eventi e
 * a soglia, non un polling a intervallo fisso.
 */
export const useSlaRuntimeRefresh = (
  needsRefetch: (nowMs: number) => boolean,
  onRefresh: () => void,
  tickMs: number = SLA_RUNTIME_REFRESH_TICK_MS,
): { nowMs: number } => {
  const [nowMs, setNowMs] = useState<number>(() => Date.now());

  /**
   * Il callback vive in un ref: i listener DOM non si ri-sottoscrivono a ogni
   * render e l'identita' della funzione inline del consumatore non conta.
   */
  const refreshRef = useRef<() => void>(onRefresh);
  useEffect(() => {
    refreshRef.current = onRefresh;
  }, [onRefresh]);

  const requestRefresh = useCallback(() => {
    refreshRef.current();
  }, []);

  /** Orologio locale: misura il trascorso reale, non il tempo lavorativo. */
  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), tickMs);
    return () => window.clearInterval(timer);
  }, [tickMs]);

  /** Rientro sul tab: stessi eventi della superficie Attivita' (F3c/F3d). */
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") requestRefresh();
    };
    const onFocus = () => requestRefresh();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    window.addEventListener("pageshow", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("pageshow", onFocus);
    };
  }, [requestRefresh]);

  /**
   * Soglia: `thresholdReached` e' un booleano, quindi la rilettura parte al
   * **passaggio** a vero e non si ripete a ogni tick finche' il payload continua
   * a dichiarare la stessa soglia gia' superata (nessun ciclo di richieste).
   */
  const thresholdReached = needsRefetch(nowMs);
  useEffect(() => {
    if (thresholdReached) requestRefresh();
  }, [thresholdReached, requestRefresh]);

  return { nowMs };
};

export default useSlaRuntimeRefresh;
